import { useCallback } from 'react';
import { useTRPCAPI, useTRPCQuery, useTRPCMutation } from '../../../trpc/trpc';
import { useShareTokenContext } from '../contexts/ShareTokenContext';

interface UsePlaylistFollowProps {
  playlistId: string;
}

export const usePlaylistFollow = ({ playlistId }: UsePlaylistFollowProps) => {
  const trpcMusicAPI = useTRPCAPI();
  const { isPublicMode } = useShareTokenContext();

  // In public (share-token) mode this endpoint is protected, so skip the query entirely.
  const isFollowingQuery = useTRPCQuery(
    trpcMusicAPI.playlists.isFollowing.queryOptions({ playlistId }, { enabled: Boolean(playlistId) && !isPublicMode })
  );

  const followMutation = useTRPCMutation(
    trpcMusicAPI.playlists.follow.mutationOptions({
      onSuccess: () => {
        isFollowingQuery.refetch().catch(console.error);
      },
    })
  );

  const unfollowMutation = useTRPCMutation(
    trpcMusicAPI.playlists.unfollow.mutationOptions({
      onSuccess: () => {
        isFollowingQuery.refetch().catch(console.error);
      },
    })
  );

  const toggleFollow = useCallback(() => {
    // In public/share-token mode follow/unfollow mutations are protected. Defense-in-depth:
    // the button is also disabled, but bail out here to avoid firing a guaranteed 401.
    if (isPublicMode) {
      return;
    }
    if (isFollowingQuery.data?.isFollowing === true) {
      unfollowMutation.mutate({ playlistId });
    } else {
      followMutation.mutate({ playlistId });
    }
  }, [isPublicMode, isFollowingQuery.data?.isFollowing, playlistId, followMutation, unfollowMutation]);

  return {
    isFollowing: isFollowingQuery.data?.isFollowing ?? false,
    isLoading: isFollowingQuery.isLoading || followMutation.isPending || unfollowMutation.isPending,
    toggleFollow,
  };
};
